/**
 * Home Landing Page
 *
 * Public homepage with above-the-fold hero/video and lazy-loaded sections
 * (features, timeline, pricing, contact, footer). Adds rich SEO structured data.
 */
import React, { Suspense, lazy } from 'react';
import SEOHead from '@/components/shared/SEOHead';
import { HeroSection } from '@/components/landing/sections/HeroSection/HeroSection';
import { DemoVideoSection } from '@/components/landing/sections/DemoVideoSection/DemoVideoSection';
import { generateFAQStructuredData, COMMON_FAQS } from '@/utils/analytics/seo';
// Import performance utilities for scroll performance testing
import '@/utils/analytics/performanceUtils';

// Error boundary component for lazy loading failures
class LazyLoadErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    console.error('Lazy load error:', error);
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Lazy loading failed:', error, errorInfo);
    // You could also log this to an error reporting service
  }

  render() {
    if (this.state.hasError) {
      // Return a fallback UI instead of the failed component
      return <div className="h-20"></div>;
    }

    return this.props.children;
  }
}

// Helper function to create resilient lazy imports with error handling
const createResilientLazyImport = (importFn, componentName) => {
  return lazy(() => 
    importFn()
      .catch(error => {
        console.error(`Failed to load ${componentName}:`, error);
        // Return a fallback component on error
        return { default: () => <div className="h-20"></div> };
      })
  );
};

// OPTIMIZATION: Lazy load below-the-fold sections for faster initial render with error handling
const PricingSection = createResilientLazyImport(
  () => import('@/components/landing/sections/PricingSection/PricingSection').then(module => ({ default: module.PricingSection })),
  'PricingSection'
);
const ContactSection = createResilientLazyImport(
  () => import('@/components/landing/sections/ContactSection/ContactSection').then(module => ({ default: module.ContactSection })),
  'ContactSection'
);
const Footer = createResilientLazyImport(
  () => import('@/components/landing/sections/Footer/Footer').then(module => ({ default: module.Footer })),
  'Footer'
);
const TemplatesLanding = createResilientLazyImport(
  () => import('@/components/landing/pages/TemplatesLanding/TemplatesLanding').then(module => ({ default: module.TemplatesLanding })),
  'TemplatesLanding'
);

const TimelineSection = createResilientLazyImport(
  () => import('@/components/landing/sections/TimelineSection/TimelineSection').then(module => ({ default: module.TimelineSection })),
  'TimelineSection'
);
const FeaturesSection = createResilientLazyImport(
  () => import('@/components/landing/sections/FeaturesSection/FeaturesSection').then(module => ({ default: module.FeaturesSection })),
  'FeaturesSection'
);
const DoneForYouSection = createResilientLazyImport(
  () => import('@/components/landing/sections/DoneForYouSection/DoneForYouSection').then(module => ({ default: module.DoneForYouSection })),
  'DoneForYouSection'
);

export default function Landing() {
  // Enhanced FAQ data for homepage
  const homepageFAQs = [
    ...COMMON_FAQS,
    {
      question: "How quickly can I create political graphics?",
      answer: "With Advocacy Lab's templates, you can create professional political graphics in minutes. Simply choose a template, customize it with your content, and download."
    },
    {
      question: "Are the templates suitable for all types of political campaigns?",
      answer: "Yes! Our templates are designed for federal, state, and local campaigns, as well as advocacy organizations, ballot initiatives, and political causes."
    },
    {
      question: "Can I use these templates for commercial purposes?",
      answer: "Absolutely! All templates come with commercial usage rights, perfect for political consultants, agencies, and professional campaign organizations."
    }
  ];

  const faqStructuredData = generateFAQStructuredData(homepageFAQs);

  // Enhanced structured data for homepage
  const enhancedStructuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://advocacylab.com/#organization",
        "name": "Advocacy Lab",
        "url": "https://advocacylab.com",
        "logo": {
          "@type": "ImageObject",
          "url": "https://advocacylab.com/images/logos/logo-big-colored.png",
          "width": 512,
          "height": 512
        },
        "description": "Professional political creative platform providing 500+ templates for campaigns and advocacy groups",
        "foundingDate": "2023",
        "industry": "Political Technology",
        "areaServed": "United States",
        "knowsAbout": ["Political Marketing", "Campaign Design", "Advocacy Graphics", "Election Materials"],
        "sameAs": [
          "https://twitter.com/advocacylab",
          "https://linkedin.com/company/advocacylab"
        ]
      },
      {
        "@type": "WebSite",
        "@id": "https://advocacylab.com/#website",
        "url": "https://advocacylab.com",
        "name": "Advocacy Lab",
        "description": "The fastest way to launch high-impact political creative. Featuring 500+ templates for campaigns and causes.",
        "publisher": {
          "@id": "https://advocacylab.com/#organization"
        },
        "inLanguage": "en-US",
        "potentialAction": {
          "@type": "SearchAction",
          "target": "https://advocacylab.com/templates?search={search_term_string}",
          "query-input": "required name=search_term_string"
        }
      },
      {
        "@type": "SoftwareApplication",
        "@id": "https://advocacylab.com/#software",
        "name": "Advocacy Lab Platform",
        "applicationCategory": "Design Tool",
        "operatingSystem": "Web",
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "USD",
          "availability": "https://schema.org/InStock"
        },
        "description": "Professional political creative platform with 500+ templates for campaigns, elections, and advocacy work",
        "featureList": [
          "500+ Political Templates",
          "Drag & Drop Editor",
          "Multiple File Formats",
          "Brand Customization",
          "Print & Digital Ready"
        ],
        "screenshot": "https://advocacylab.com/images/screenshots/screen1.png",
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "4.8",
          "ratingCount": "250",
          "bestRating": "5"
        },
        "publisher": {
          "@id": "https://advocacylab.com/#organization"
        }
      },
      faqStructuredData
    ]
  };

  return (
    <div className="relative min-h-screen">
      <SEOHead 
        pageType="home"
        structuredDataOverride={enhancedStructuredData}
        breadcrumbs={[
          { name: "Home", url: "https://advocacylab.com" }
        ]}
      />
      
      {/* CRITICAL: Above-the-fold content loads immediately */}
      <HeroSection />
      <DemoVideoSection />
      
      {/* OPTIMIZATION: Below-the-fold content lazy loads for faster initial render */}
      <LazyLoadErrorBoundary>
        <Suspense fallback={<div className="h-20"></div>}>
          <TemplatesLanding />
        </Suspense>
      </LazyLoadErrorBoundary>
      
      <LazyLoadErrorBoundary>
        {/* Reserve realistic height for FeaturesSection to avoid scroll jumps */}
        <Suspense fallback={<div className="w-full" style={{ minHeight: 900 }}></div>}>
          <FeaturesSection />
        </Suspense>
      </LazyLoadErrorBoundary>
      
      <LazyLoadErrorBoundary>
        <Suspense fallback={<div className="h-20"></div>}>
          <TimelineSection />
        </Suspense>
      </LazyLoadErrorBoundary>
      
      <LazyLoadErrorBoundary>
        <Suspense fallback={<div className="h-20"></div>}>
          <PricingSection />
        </Suspense>
      </LazyLoadErrorBoundary>
      
      <LazyLoadErrorBoundary>
        <Suspense fallback={<div className="h-20"></div>}>
          <DoneForYouSection />
        </Suspense>
      </LazyLoadErrorBoundary>
      
      <LazyLoadErrorBoundary>
        <Suspense fallback={<div className="h-20"></div>}>
          <ContactSection />
        </Suspense>
      </LazyLoadErrorBoundary>
      
      <LazyLoadErrorBoundary>
        <Suspense fallback={<div className="h-20"></div>}>
          <Footer />
        </Suspense>
      </LazyLoadErrorBoundary>
    </div>
  );
}